import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  LogOut, 
  Coffee, 
  Clock, 
  CheckCircle,
  AlertTriangle,
  MapPin,
  Loader2
} from 'lucide-react';
import { formatTimeFromISO } from '@/utils/timezoneUtils';
import { useLanguage } from '@/i18n/LanguageContext';
import {
  formatLocalDate,
  formatLocalTimeWithSeconds,
  formatShiftTimeLocal,
  getDeviceTimeZone,
} from '@/utils/dateUtils';

function StaffDashboardHome({ onStatusChange, onNotificationUpdate }) {
  const { t, isRTL } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [checkInDialogOpen, setCheckInDialogOpen] = useState(false);
  const [checkOutDialogOpen, setCheckOutDialogOpen] = useState(false);
  const [forceBreakAlert, setForceBreakAlert] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);

  useEffect(() => {
    fetchStats();
    checkForceBreakStatus();

    const interval = setInterval(fetchStats, 30000);
    const forceBreakInterval = setInterval(checkForceBreakStatus, 10000);
    const timeInterval = setInterval(() => setCurrentTime(new Date()), 1000);

    return () => {
      clearInterval(interval);
      clearInterval(forceBreakInterval);
      clearInterval(timeInterval);
    };
  }, []);
  
  const checkForceBreakStatus = async () => {
    try {
      const response = await axios.get(`${API}/staff/force-break-status`, {
        withCredentials: true
      });
      if (response.data.on_force_break && !forceBreakAlert) {
        setForceBreakAlert(response.data);
      } else if (!response.data.on_force_break && forceBreakAlert) {
        setForceBreakAlert(null);
      }
    } catch (error) {
      console.error('Failed to check force break status:', error);
    }
  };
  
  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/staff/dashboard/stats`, {
        withCredentials: true
      });
      setStats(response.data);
      // Notify parent component about status change
      if (onStatusChange) {
        onStatusChange();
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCheckIn = async () => {
    setActionLoading(true);
    setError('');
    setSuccess('');
    setCheckInDialogOpen(false);

    // Only request GPS if check-in GPS coordinates are configured for this staff
    let coords = null;
    if (stats?.gps_config?.has_checkin && navigator.geolocation) {
      setGpsLoading(true);
      try {
        coords = await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
            () => resolve(null),  // GPS denied/unavailable — backend decides if it's required
            { timeout: 8000, maximumAge: 30000 }
          );
        });
      } finally {
        setGpsLoading(false);
      }
    }

    try {
      const response = await axios.post(
        `${API}/staff/check-in`,
        coords ? { lat: coords.lat, lng: coords.lng } : {},
        { withCredentials: true }
      );
      setSuccess('Checked in successfully!');
      setTimeout(() => { fetchStats(); }, 500);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Check-in failed';
      // Show friendly messages for GPS errors
      if (detail.startsWith('GPS_REQUIRED:')) {
        setError(detail.replace('GPS_REQUIRED: ', ''));
      } else if (detail.startsWith('GPS_OUT_OF_RANGE:')) {
        setError(detail.replace('GPS_OUT_OF_RANGE: ', ''));
      } else {
        setError(detail);
      }
    } finally {
      setActionLoading(false);
      setGpsLoading(false);
    }
  };
  
  const handleCheckOut = async () => {
    setActionLoading(true);
    setError('');
    setSuccess('');
    setCheckOutDialogOpen(false);

    // Only request GPS if check-out GPS coordinates are configured for this staff
    let coords = null;
    if (stats?.gps_config?.has_checkout && navigator.geolocation) {
      setGpsLoading(true);
      try {
        coords = await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
            () => resolve(null),
            { timeout: 8000, maximumAge: 30000 }
          );
        });
      } finally {
        setGpsLoading(false);
      }
    }

    try {
      const response = await axios.post(
        `${API}/staff/check-out`,
        coords ? { lat: coords.lat, lng: coords.lng } : {},
        { withCredentials: true }
      );
      setSuccess('Checked out successfully!');
      setTimeout(() => { fetchStats(); }, 500);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Check-out failed';
      if (detail.startsWith('GPS_REQUIRED:')) {
        setError(detail.replace('GPS_REQUIRED: ', ''));
      } else if (detail.startsWith('GPS_OUT_OF_RANGE:')) {
        setError(detail.replace('GPS_OUT_OF_RANGE: ', ''));
      } else {
        setError(detail);
      }
    } finally {
      setActionLoading(false);
      setGpsLoading(false);
    }
  };
  
  const handleStartBreak = async () => {
    setActionLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await axios.post(`${API}/staff/break/start`, {}, {
        withCredentials: true
      });
      
      console.log('Start break response:', response);
      setSuccess('Break started!');
      
      setTimeout(() => {
        fetchStats();
      }, 500);
      
    } catch (error) {
      console.error('Start break error:', error);
      setError(error.response?.data?.detail || 'Failed to start break');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleEndBreak = async () => {
    setActionLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await axios.post(`${API}/staff/break/end`, {}, {
        withCredentials: true
      });
      
      console.log('End break response:', response);
      setSuccess('Break ended!');
      
      setTimeout(() => {
        fetchStats();
      }, 500);
      
    } catch (error) {
      console.error('End break error:', error);
      setError(error.response?.data?.detail || 'Failed to end break');
    } finally {
      setActionLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  const attendance = stats?.today_attendance;
  const todaySessions = stats?.today_sessions || [];
  const hasCheckedIn = stats?.is_checked_in;
  const hasCheckedOut = stats?.is_checked_out;
  const onBreak = stats?.on_break;
  
  const formatTime = (date) => {
    // Format time from ISO string - already converted to staff timezone by backend
    if (!date) return '-';
    return formatTimeFromISO(date);
  };
  
  const staffTimezone = stats?.staff?.timezone || 'UTC';
  const deviceTimezone = getDeviceTimeZone();
  const shiftStartLocal = formatShiftTimeLocal(stats?.staff?.shift_start, staffTimezone);
  const shiftEndLocal = formatShiftTimeLocal(stats?.staff?.shift_end, staffTimezone);
  const isLate = (attendance?.late_by || 0) > 0 || attendance?.status === 'late';
  
  const getStaffLocalTime = () => {
    // Device/browser local time (UTC instant → viewer local)
    return formatLocalTimeWithSeconds(currentTime);
  };

  const getStaffLocalDate = () => {
    // Device/browser local date
    return formatLocalDate(currentTime);
  };

  
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full space-y-8">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-4xl font-bold text-gray-900">
            Welcome, {stats?.staff?.name?.split(' ')[0]}!
          </h1>
          <p className="text-gray-500">
            {getStaffLocalDate()}
          </p>
          <p className="text-3xl font-semibold text-indigo-600">
            {getStaffLocalTime()}
          </p>
          <p className="text-xs text-gray-400">
            Times shown in your local timezone ({deviceTimezone})
            {staffTimezone !== deviceTimezone ? ` · Shift schedule converted from ${staffTimezone}` : ''}
          </p>
        </div>
        
        {/* Alerts */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl text-sm flex items-start gap-2">
            {(error.toLowerCase().includes('gps') || error.toLowerCase().includes('location') || error.toLowerCase().includes('away')) ? (
              <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-red-500" />
            ) : (
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-red-500" />
            )}
            <span>{error}</span>
          </div>
        )}
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-xl text-center">
            {success}
          </div>
        )}
        
        {/* Manual Force Break Alert */}
        {forceBreakAlert && (
          <div className="bg-purple-50 border-2 border-purple-300 text-purple-900 px-6 py-4 rounded-xl text-center animate-pulse" data-testid="force-break-alert">
            <div className="flex items-center justify-center gap-3">
              <AlertTriangle className="w-6 h-6 text-purple-600" />
              <div>
                <p className="font-bold text-lg">Manual Force Break</p>
                <p className="text-sm">You have been placed on a break by your administrator.</p>
                <p className="text-xs text-purple-600 mt-1">
                  Started at: {forceBreakAlert.started_at ? formatTimeFromISO(forceBreakAlert.started_at) : 'Just now'}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Shift Info Card */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-50 rounded-full">
                <Clock className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Your Shift</p>
                <p className="text-2xl font-bold text-gray-900">
                  {shiftStartLocal} - {shiftEndLocal}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Checked In</p>
              <p className={`text-xl font-bold ${isLate ? 'text-red-600' : 'text-green-600'}`}>
                {hasCheckedIn ? formatTime(attendance.check_in) : '--:--'}
              </p>
              {isLate && (
                <span className="inline-block mt-1 px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                  Late by {attendance.late_by || 0} min
                </span>
              )}
            </div>
          </div>
        </div>
        
        {/* Action Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Check In/Out Card */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${hasCheckedIn ? 'bg-green-50' : 'bg-gray-50'}`}>
                <CheckCircle className={`w-5 h-5 ${hasCheckedIn ? 'text-green-600' : 'text-gray-400'}`} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {hasCheckedIn ? 'Checked In' : 'Not Checked In'}
                </h3>
                <p className="text-sm text-gray-500">
                  {hasCheckedIn ? 'Remember to check out when you leave' : 'Click below to start your day'}
                </p>
              </div>
            </div>
            
            {!hasCheckedIn ? (
              <Button
                className="w-full h-12 text-base bg-indigo-600 hover:bg-indigo-700"
                onClick={() => setCheckInDialogOpen(true)}
                disabled={actionLoading || gpsLoading}
                data-testid="check-in-btn"
              >
                {gpsLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Getting location...</>
                ) : (
                  'Check In'
                )}
              </Button>
            ) : !hasCheckedOut ? (
              <Button
                variant="outline"
                className="w-full h-12 text-base border-2 border-red-300 text-red-600 hover:bg-red-50"
                onClick={() => setCheckOutDialogOpen(true)}
                disabled={onBreak || actionLoading}
                data-testid="check-out-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Check Out
              </Button>
            ) : (
              <div className="space-y-3">
                <div className="text-center py-2 text-green-600 font-medium">
                  ✓ Checked Out
                </div>
                <Button
                  className="w-full h-12 text-base bg-indigo-600 hover:bg-indigo-700"
                  onClick={() => setCheckInDialogOpen(true)}
                  disabled={actionLoading || gpsLoading}
                  data-testid="check-in-again-btn"
                >
                  {gpsLoading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Getting location...</>
                  ) : (
                    'Check In Again'
                  )}
                </Button>
              </div>
            )}
          </div>
          
          {/* Breaks Card */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${onBreak ? 'bg-purple-50' : 'bg-gray-50'}`}>
                <Coffee className={`w-5 h-5 ${onBreak ? 'text-purple-600' : 'text-gray-400'}`} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Breaks</h3>
                <p className="text-sm text-gray-500">
                  {onBreak ? 'Currently on break' : 'No active break'}
                </p>
              </div>
            </div>
            
            {onBreak ? (
              <Button
                variant="outline"
                className="w-full h-12 text-base border-2 border-gray-300 hover:bg-gray-50"
                onClick={handleEndBreak}
                disabled={actionLoading}
              >
                <Clock className="w-4 h-4 mr-2" />
                End Break
              </Button>
            ) : (
              <Button
                variant="outline"
                className="w-full h-12 text-base border-2 border-gray-300 hover:bg-gray-50"
                onClick={handleStartBreak}
                disabled={!hasCheckedIn || hasCheckedOut || actionLoading}
              >
                <Coffee className="w-4 h-4 mr-2" />
                Start Break
              </Button>
            )}
          </div>
        </div>
        
        {/* Today's Breaks */}
        {attendance?.breaks && attendance.breaks.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Today&apos;s Breaks</h3>
            <div className="space-y-2">
              {attendance.breaks.map((brk, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
                      Break {index + 1}
                    </span>
                    <span className="text-gray-700">
                      {formatTime(brk.start_time)} - {brk.end_time ? formatTime(brk.end_time) : 'ongoing'}
                    </span>
                  </div>
                  {brk.duration > 0 && (
                    <span className="text-gray-600 font-medium">{brk.duration} min</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Today's Sessions History */}
        {todaySessions.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-200 p-6" data-testid="sessions-history">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Today&apos;s Sessions ({todaySessions.length})</h3>
            <div className="space-y-2">
              {todaySessions.map((session, index) => (
                <div 
                  key={session.attendance_id || index} 
                  className={`flex items-center justify-between p-3 rounded-xl ${
                    !session.check_out ? 'bg-green-50 border-2 border-green-200' : 'bg-blue-50'
                  }`} 
                  data-testid={`session-${index}`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      !session.check_out ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {!session.check_out ? 'Current' : `Session ${session.session_number || index + 1}`}
                    </span>
                    <span className="text-gray-700">
                      {formatTime(session.check_in)} - {session.check_out ? formatTime(session.check_out) : 'ongoing'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {session.late_by > 0 && (
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs">
                        Late {session.late_by}m
                      </span>
                    )}
                    <span className={`px-2 py-1 rounded text-xs ${
                      session.status === 'present' ? 'bg-green-100 text-green-700' : 
                      session.status === 'late' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {session.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Check In Confirmation Dialog */}
        <AlertDialog open={checkInDialogOpen} onOpenChange={setCheckInDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirm Check In</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to check in now?
                <br />
                <br />
                <strong>Time:</strong> {getStaffLocalTime()}
                <br />
                <strong>Date:</strong> {getStaffLocalDate()}
                <br />
                <strong>Timezone:</strong> {stats?.staff?.timezone || 'UTC'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleCheckIn}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                Yes, Check In
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        
        {/* Check Out Confirmation Dialog */}
        <AlertDialog open={checkOutDialogOpen} onOpenChange={setCheckOutDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirm Check Out</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to check out now? This will end your workday and close any open breaks.
                <br />
                <br />
                <strong>Time:</strong> {getStaffLocalTime()}
                <br />
                <strong>Date:</strong> {getStaffLocalDate()}
                <br />
                <strong>Timezone:</strong> {stats?.staff?.timezone || 'UTC'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleCheckOut}
                className="bg-red-600 hover:bg-red-700"
              >
                Yes, Check Out
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

export default StaffDashboardHome;
